import React from 'react'

import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { mergeDeepRight } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import config from '~/src/App/config'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import { renderableType } from '~/src/Lib/PropTypes'
import safeStorage from '~/src/Lib/safeStorage'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '~/src/Lib/Utils'
import Null from '~/src/UI/Shared/Null'

const storage = safeStorage()
const notProd = process.env.NODE_ENV !== 'production' || config.ENVIRONMENT !== 'production'
const getMergedFlags = memoizeOne(mergeDeepRight)
const logger = createLogger('Has')

const global = getGlobal()
let testFlags
/* eslint-disable no-underscore-dangle */
if (global && notProd) {
  global.__flagsChecked__ = {}
  const storedFlags = storage.getItem('testFlags')
  if (storedFlags) {
    try {
      testFlags = JSON.parse(storedFlags)
    } catch (error) {
      console.warn(error)
    }
  }
}
/* eslint-enable no-underscore-dangle */

export const available = (myFlags = EMPTY_OBJECT, features = EMPTY_ARRAY, any = false) => {
  let flags = myFlags
  let pageChecked
  if (notProd) {
    const { __flagsChecked__, location = EMPTY_OBJECT } = global ?? EMPTY_OBJECT
    pageChecked = __flagsChecked__[location.pathname] ?? (__flagsChecked__[location.pathname] = {})
    if (testFlags) {
      flags = getMergedFlags(myFlags, testFlags)
    }
  }
  const requiredFeatures = Array.isArray(features) ? features : Array.of(features)
  logger.debug({ allowedFeatures: flags, requiredFeatures, any })
  return requiredFeatures[any ? 'some' : 'every'](flag => {
    const has = typeof flag === 'function' ? flag(flags) : Boolean(flags?.[flag])

    if (pageChecked && flag) {
      pageChecked[typeof flag === 'function' ? flag?.name : flag] = has
    }
    return has
  })
}
/* eslint-enable babel/camelcase, no-underscore-dangle */

export const useFlags = () => {
  const { myFlags } = useConnect('selectMyFlags')
  if (notProd && testFlags) {
    return getMergedFlags(myFlags, testFlags)
  }
  return myFlags
}

export const useHas = (features, any) => {
  const { myFlags } = useConnect('selectMyFlags')
  if (!features.length) return true
  return available(myFlags, features, any)
}

export const HasComponent = ({
  has,
  children = null,
  Fallback = Null,
  FallbackProps = EMPTY_OBJECT
}) => {
  if (has) return children
  return React.isValidElement(Fallback) ? Fallback : <Fallback {...FallbackProps} />
}

HasComponent.propTypes = {
  has: PropTypes.bool.isRequired,
  children: PropTypes.node,
  Fallback: PropTypes.oneOfType([renderableType, PropTypes.element]),
  FallbackProps: PropTypes.shape({})
}
HasComponent.displayName = 'Has'

const Memoized = React.memo(props => {
  const { flags, any, ...passthru } = props
  const has = useHas(flags, any)

  return <HasComponent {...passthru} has={has} />
})
Memoized.propTypes = {
  flags: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func,
    ])),
  ]),
  any: PropTypes.bool,
  children: PropTypes.node.isRequired,
}
Memoized.defaultProps = {
  flags: EMPTY_ARRAY,
  any: false,
}

export default Memoized
